import Box from "@mui/material/Box";
import { css, useTheme as useMuiTheme } from "@mui/material/styles";
import { theme } from "@polifonia/theme";
import { getRouteApi, Outlet, useNavigate } from "@tanstack/react-router";
import queryString from "query-string";
import { memo } from "react";
import { useEffect } from "react";
import ReactGA4 from "react-ga4";
import { useTranslation } from "react-i18next";

import { BarLoading } from "@/components/Loading/BarLoading";
import {
  AUTH_SOURCE_KEY,
  AUTH_SOURCE_MAAC,
  canRedirectToSource,
  redirectToSource,
} from "@/features/auth/redirectToMaac";
import { useLoginMutation } from "@/features/auth/useLoginMutation";
import { useTokenStore } from "@/features/auth/useTokenStore";
import { BindStatsigUser } from "@/features/statsig/BindStatsigUser";
import { interlude } from "@/interlude";
import { ErrorFallbackPage } from "@/layout/ErrorBoundary/ErrorFallbackPage";
import { Sidebar } from "@/layout/MainContent/Sidebar";
import { rootQueriesContext } from "@/queriesContext/rootQueriesContext";

const cssErrorFallbackPage = css({
  paddingTop: 0,
});

const routeApi = getRouteApi("__root__");

export const MainContent = memo(function MainContent() {
  const { t } = useTranslation();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();

  const routeSearch = routeApi.useSearch();
  const loginMutation = useLoginMutation();

  const meInfoQuery = interlude.auth.useGetMe(undefined, {
    enabled:
      !routeSearch.token &&
      (loginMutation.isSuccess || !!useTokenStore.getState().value),
    refetchOnWindowFocus: false,
  });

  useEffect(
    function initialDataExchange() {
      if (loginMutation.isPending) {
        return;
      }
      if (routeSearch.token) {
        useTokenStore.getState().clear();

        loginMutation.mutate(
          { token: routeSearch.token },
          {
            onSettled: () => {
              // Remove token search from URL after login
              const { token, ...rest } = queryString.parse(
                window.location.search,
              );
              navigate({
                to: window.location.pathname,
                search: rest,
                replace: true,
              });
            },
          },
        );
      }
    },
    [loginMutation, navigate, routeSearch.token],
  );

  useEffect(
    function setGaUserProperties() {
      if (meInfoQuery.data?.organization.uuid) {
        ReactGA4.set({
          user_properties: {
            organization_uuid: meInfoQuery.data.organization.uuid,
          },
        });
      }
    },
    [meInfoQuery.data?.organization.uuid],
  );

  // Token exchange or session query already failed — show error page.
  // Do NOT redirect back to MAAC here; the token was already attempted and
  // retrying would create an infinite redirect loop.
  if (loginMutation.isError || meInfoQuery.isError) {
    return (
      <ErrorFallbackPage
        css={cssErrorFallbackPage}
        description={t("errorPage.tryFromTheOriginalApp")}
      />
    );
  }

  // No authentication at all — try redirecting to MAAC if source=maac is
  // explicitly in the URL. sessionStorage auth-source is intentionally NOT
  // checked here — it is reserved for the 401 interceptor so that a plain
  // visit to AC (without ?source=maac) always shows the error page.
  if (!routeSearch.token && !useTokenStore.getState().value) {
    if (routeSearch.source === "maac") {
      sessionStorage.setItem(AUTH_SOURCE_KEY, AUTH_SOURCE_MAAC);
      if (canRedirectToSource()) {
        redirectToSource();
        return <BarLoading wrapperStyle={{ margin: "auto" }} />;
      }
    }

    return (
      <ErrorFallbackPage
        css={cssErrorFallbackPage}
        description={t("errorPage.tryFromTheOriginalApp")}
      />
    );
  }

  if (loginMutation.isPending || meInfoQuery.isPending) {
    return <BarLoading wrapperStyle={{ margin: "auto" }} />;
  }

  return (
    <rootQueriesContext.Provider>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minWidth: "960px",
        }}
      >
        <Sidebar organizationName={meInfoQuery.data?.organization.name} />
        <Box
          component="main"
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            padding: muiTheme.spacing(3, 5),
            background: theme.colors.neutral000,
            overflow: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <BindStatsigUser />
    </rootQueriesContext.Provider>
  );
});
